import React, { useEffect, useState } from 'react';
import { Button, Form, Spinner } from 'react-bootstrap';
import { useCheckAccessQuery } from '@/app/common/api/accessLevelApi';
import { useNavigate, useParams } from 'react-router-dom';
import { useGetUserInfoQuery } from '@/app/common/api/authApi';
import { object, string } from 'yup';
import { Formik, type FormikHelpers } from 'formik';
import { errorMessage, warningMessage } from '@/utils/toast';
import { useDispatch } from 'react-redux';
import TextLoading from '@/app/common/components/skeleton/TextLoading';
import keywordPlannerApi, {
  useStartPlannerMutation
} from '@/app/KeywordResearch/api/keywordPlannerApi';
import accessLevelApi from '@/app/common/api/accessLevelApi';
import Select, { type SingleValue } from 'react-select';
import OptionLayoutPlace from '@/app/common/components/OptionsLayout/OptionLayoutPlace';
import SingleValueFlag from '@/app/common/components/OptionsLayout/SingleValueFlag';
import geoLanguagesData from '@/app/common/api/backups/languages.json';
import geoLocationsData from '@/app/common/api/backups/locations.json';
import HelpTooltip from '@/app/common/components/HelpTooltip';
import ChildrensTooltip from '@/components/common/ChildrensTooltip';
import IconAlert from '@/components/common/IconAlert';
import {
  breakpointsMediaQuery,
  getColor,
  useMediaQuery
} from '@/helpers/utils';
import useDomain from '@/hooks/useDomain';
import classNames from 'classnames';
import { useGetTariffInfoQuery } from '@/app/KeywordTracker/api/keywordsApi';
import { toUrlKeywordSegment } from '@/app/KeywordResearch/utils/toUrlKeywordSegment';

const isLoadingGeoLanguages = false;
const isLoadingGeoLocations = false;

type SelectOption = {
  value: string;
  label: string;
};

type CountryOption = SelectOption & {
  flag?: string;
};

export type InputType = 'keyword' | 'url';

type NewPlannerFormProps = {
  onInputTypeChange?: (type: InputType) => void;
};

type NewPlannerFormValues = {
  keyword: string;
  searchType: InputType;
};

const inputTypeOptions: { value: InputType; label: string }[] = [
  { value: 'keyword', label: 'کلمه' },
  { value: 'url', label: 'آدرس' }
];

const ALL_LOCATIONS_VALUE = 'all';
const ALL_LANGUAGES_VALUE = 'all';
const GLOBAL_FLAG_URL = 'https://static.mizfa.tools/flags/global.webp';

const WORLDWIDE_OPTION: CountryOption = {
  value: ALL_LOCATIONS_VALUE,
  label: 'کل جهان',
  flag: GLOBAL_FLAG_URL
};

const ALL_LANGUAGES_OPTION: SelectOption = {
  value: ALL_LANGUAGES_VALUE,
  label: 'همه زبان ها'
};

const PLANNER_SUBMIT_ERROR_MESSAGE =
  'در ارسال درخواست خطایی رخ داده هست لطفا مجددا تلاش کنید.';

const NewPlannerForm = ({ onInputTypeChange }: NewPlannerFormProps) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isNoData, setIsNoData] = useState(false);
  const { keyword, loc, lang } = useParams<{
    keyword?: string;
    loc?: string;
    lang?: string;
  }>();
  const { domain_slug, domain } = useDomain();
  const [geoLocationsCountries, setGeoLocationsCountries] =
    useState<CountryOption[]>();
  const [geoLanguages, setGeoLanguages] = useState<SelectOption[]>();
  const [startPlanner, { isLoading }] = useStartPlannerMutation();
  const isLgUp = useMediaQuery(breakpointsMediaQuery.lg);

  const { data: userInfo } = useGetUserInfoQuery();

  const [selectedPlace, setSelectedPlace] = useState<CountryOption>({
    value: 'ir',
    label: 'ایران',
    flag: `https://static.mizfa.tools/flags/ir.webp`
  });

  const [selectedLanguage, setSelectedLanguage] = useState<SelectOption>({
    value: 'fa',
    label: 'فارسی'
  });

  const [selectedInputType, setSelectedInputType] = useState<InputType>('keyword');

  const { data: trackerInfo, isFetching: isFetchingTrackerInfo } =
    useGetTariffInfoQuery(domain_slug);
  const isAccessAllowed = () =>
    trackerInfo?.data?.access_level !== 3 && !isFetchingTrackerInfo;

  useEffect(() => {
    if (geoLocationsData) {
      const geoCountries: CountryOption[] = [WORLDWIDE_OPTION];
      geoLocationsData.data.map(({ code, fa_name }) =>
        geoCountries.push({
          value: code,
          label: fa_name,
          flag: `https://static.mizfa.tools/flags/${code}.webp`
        })
      );

      setGeoLocationsCountries(geoCountries);

      if (loc === ALL_LOCATIONS_VALUE) {
        setSelectedPlace(WORLDWIDE_OPTION);
      } else if (loc && loc !== 'ir') {
        const geo = geoLocationsData.data.find(item => item.code === loc);

        if (geo) {
          setSelectedPlace({
            value: geo.code,
            label: geo.fa_name,
            flag: `https://static.mizfa.tools/flags/${geo.code}.webp`
          });
        }
      }
    }
  }, [loc]);

  useEffect(() => {
    if (geoLanguagesData) {
      const languages: SelectOption[] = [ALL_LANGUAGES_OPTION];
      geoLanguagesData.data.map(({ code, fa_name }) =>
        languages.push({
          value: code,
          label: fa_name
        })
      );

      setGeoLanguages(languages);

      if (lang === ALL_LANGUAGES_VALUE) {
        setSelectedLanguage(ALL_LANGUAGES_OPTION);
      } else if (lang && lang !== 'fa') {
        const geo = geoLanguagesData.data.find(item => item.code === lang);

        if (geo) {
          setSelectedLanguage({
            value: geo.code,
            label: geo.fa_name
          });
        }
      }
    }
  }, [lang]);

  const { data: accessData } = useCheckAccessQuery({
    domain_slug,
    tool_name: 'keyword_planner'
  });

  const onSubmit = async (
    values: NewPlannerFormValues,
    _actions: FormikHelpers<NewPlannerFormValues>
  ) => {
    await startPlanner({
      requested_user: {
        name_family: `${userInfo?.data?.first_name ?? ''} ${userInfo?.data?.last_name ?? ''}`.trim(),
        email: userInfo?.data?.email ?? ''
      },
      keyword: selectedInputType === 'keyword' ? values.keyword : null,
      url: selectedInputType === 'url' ? values.keyword : null,
      location_code:
        selectedPlace.value === ALL_LOCATIONS_VALUE
          ? null
          : selectedPlace.value,
      language_code:
        selectedLanguage.value === ALL_LANGUAGES_VALUE
          ? null
          : selectedLanguage.value,
      domain_slug
    })
      .unwrap()
      .then(response => {
        if (response.code !== 200 && response.code !== 201) {
          warningMessage(String(response.data));
        } else if (
          typeof response.data === 'object' &&
          response.data?.task_id
        ) {
          if (response.data.status === 'failed') {
            errorMessage(PLANNER_SUBMIT_ERROR_MESSAGE);
          } else {
            const navKeyword = toUrlKeywordSegment(
              response.data.keyword ?? values.keyword
            );
            navigate(
              `/keyword-research/planner/${domain_slug}/${domain}/${response.data.task_id}/${selectedPlace.value}/${selectedLanguage.value}/${navKeyword}`
            );
            dispatch(accessLevelApi.util.invalidateTags(['AccessLevel']));
          }
          dispatch(keywordPlannerApi.util.invalidateTags(['PlannerHistory']));
        } else {
          setIsNoData(true);
        }
      })
      .catch(err => {
        errorMessage(PLANNER_SUBMIT_ERROR_MESSAGE);
        console.log(err);
      });
  };

  return (
    <>
      {!isLoadingGeoLanguages && !isLoadingGeoLocations ? (
        <>
          <Formik<NewPlannerFormValues>
            onSubmit={onSubmit}
            initialValues={{
              keyword: keyword !== 'last' ? (keyword ?? '') : '',
              searchType: 'keyword'
            }}
            validationSchema={object().shape({
              searchType: string()
                .oneOf(['keyword', 'url'])
                .required(),
              keyword: string().when('searchType', {
                is: 'url',
                then: schema =>
                  schema
                    .required('وارد کردن آدرس الزامی می‌باشد')
                    .url(
                      'آدرس معتبر نیست (باید با http یا https شروع بشه).'
                    ),
                otherwise: schema =>
                  schema.required('وارد کردن کلمه الزامی می‌باشد')
              })
            })}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              handleSubmit,
              setFieldValue
            }) => (
              <div className={`w-100 w-lg-75 mt-3 mb-3`}>
                <Form onSubmit={handleSubmit}>
                  <div
                    className={classNames('position-relative input-group-md', {
                      'input-group': isLgUp,
                      'd-flex flex-column w-100 gap-2': !isLgUp
                    })}
                  >
                    <Form.Select
                      value={selectedInputType}
                      disabled={isLoading}
                      onChange={e => {
                        const value = e.target.value as InputType;
                        setSelectedInputType(value);
                        setFieldValue('searchType', value);
                        onInputTypeChange?.(value);
                      }}
                      style={{
                        width: isLgUp ? '120px' : '100%',
                        flex: '0 0 auto',
                        borderRadius: isLgUp ? '0 5px 5px 0' : '5px',
                        cursor: 'pointer'
                      }}
                    >
                      {inputTypeOptions.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Form.Select>
                    <div style={{ width: isLgUp ? '160px' : '100%' }}>
                      <Select<CountryOption>
                        menuShouldScrollIntoView={true}
                        closeMenuOnSelect={true}
                        isSearchable={true}
                        onChange={(e: SingleValue<CountryOption>) => {
                          if (!e) return;
                          setSelectedPlace(e);

                          if (e.value === ALL_LOCATIONS_VALUE) {
                            return;
                          }

                          const location = geoLocationsData.data.find(
                            item => item.code === e.value
                          );
                          const { official_locaguage_code } = location ?? {
                            official_locaguage_code: null
                          };

                          let defaultLanguage = {
                            code: 'en',
                            fa_name: 'انگلیسی'
                          };
                          if (official_locaguage_code) {
                            const language = geoLanguagesData.data.find(
                              item => item.code === official_locaguage_code
                            );
                            if (language) defaultLanguage = language;
                          }

                          setSelectedLanguage({
                            value: defaultLanguage.code,
                            label: defaultLanguage.fa_name
                          });
                        }}
                        options={geoLocationsCountries}
                        classNamePrefix="react-select"
                        value={selectedPlace}
                        maxMenuHeight={300}
                        styles={{
                          control: baseStyles => ({
                            ...baseStyles,
                            paddingLeft: '0px',
                            paddingRight: '10px',
                            color: 'var(--falcon-emphasise) !important',
                            boxShadow: 'none !important',
                            cursor: 'pointer',
                            borderRadius: isLgUp ? '0px' : '5px'
                          }),
                          input: baseStyles => ({
                            ...baseStyles,
                            color: 'var(--falcon-emphasise) !important',
                            boxShadow: 'none !important'
                          }),
                          menu: base => ({
                            ...base,
                            width: '200px'
                          })
                        }}
                        components={{
                          Option: OptionLayoutPlace,
                          SingleValue: SingleValueFlag
                        }}
                      />
                    </div>
                    <div style={{ width: isLgUp ? '120px' : '100%' }}>
                      <Select<SelectOption>
                        menuShouldScrollIntoView={true}
                        closeMenuOnSelect={true}
                        isSearchable={true}
                        onChange={(e: SingleValue<SelectOption>) => {
                          if (e) setSelectedLanguage(e);
                        }}
                        options={geoLanguages}
                        classNamePrefix="react-select"
                        value={selectedLanguage}
                        maxMenuHeight={300}
                        styles={{
                          control: baseStyles => ({
                            ...baseStyles,
                            paddingLeft: '0px',
                            paddingRight: '10px',
                            color: 'var(--falcon-emphasise) !important',
                            boxShadow: 'none !important',
                            cursor: 'pointer',
                            borderRadius: isLgUp ? '0px' : '5px'
                          }),
                          input: baseStyles => ({
                            ...baseStyles,
                            color: 'var(--falcon-emphasise) !important',
                            boxShadow: 'none !important'
                          }),
                          menu: base => ({
                            ...base,
                            width: '150px'
                          })
                        }}
                        components={{
                          Option: OptionLayoutPlace,
                          SingleValue: SingleValueFlag
                        }}
                      />
                    </div>

                    <Form.Control
                      type="text"
                      isInvalid={!!(touched.keyword && errors.keyword)}
                      isValid={!!(touched.keyword && !errors.keyword)}
                      placeholder={
                        selectedInputType === 'url'
                          ? 'آدرس صفحه را وارد کنید (مثال: https://example.com/page)'
                          : 'کلمه مدنظر را جستجو کنید'
                      }
                      value={values.keyword}
                      onChange={handleChange}
                      disabled={isLoading}
                      onBlur={handleBlur}
                      name="keyword"
                    />
                    <ChildrensTooltip
                      placement="top"
                      className="p-0"
                      html={
                        isAccessAllowed() ? '' : 'شما دسترسی لازم را ندارید'
                      }
                    >
                      <Button
                        variant="falcon-primary"
                        className={
                          isLgUp
                            ? 'd-flex align-items-center h-100 rounded-start-0'
                            : 'w-100'
                        }
                        type="submit"
                        disabled={
                          !isAccessAllowed() ||
                          isLoading ||
                          (accessData?.data?.budget_used ?? 0) >=
                            (accessData?.data?.total_budget ?? 0)
                        }
                      >
                        <span className="me-1 fs--1">
                          {isLoading ? (
                            <>
                              <Spinner
                                style={{
                                  width: '16px',
                                  height: '16px'
                                }}
                                className="me-1"
                              />
                              درحال اجرا
                            </>
                          ) : (
                            <>
                              <span className="fs-0 me-1">اجرا</span>
                              <span className="opacity-75 fs--1">
                                (
                                {accessData?.data?.budget_used
                                  ?.toString()
                                  ?.toPersianDigit()}{' '}
                                از
                                {accessData?.data?.total_budget
                                  ?.toString()
                                  ?.toPersianDigit()}
                                <HelpTooltip
                                  color={getColor('black')}
                                  placement="top"
                                  html="این محدودیت ماهانه است."
                                />
                                )
                              </span>
                            </>
                          )}
                        </span>
                      </Button>
                    </ChildrensTooltip>
                  </div>

                  <div>
                    {errors.keyword ? (
                      <Form.Label className="text-danger">
                        {errors.keyword}
                      </Form.Label>
                    ) : null}
                  </div>
                </Form>
              </div>
            )}
          </Formik>
          {isNoData && (
            <IconAlert variant="warning">
              برای کلمه شما داده‌ای یافت نشد، بهتره کلمه دیگه رو تست کنید یا
              برای این کلمه از تب کلمات انبوه استفاده کنید، در نظر داشته باشید
              کیورد پلنر گوگل برای برخی کلمات پزشکی محدودیت داره{' '}
              <a
                target="_blank"
                rel="noreferrer noopener"
                href="https://help.mizfa.tools/reason-for-not-displaying-keyword-search-volume/"
              >
                (اطلاعات بیشتر)
              </a>
              .{' '}
            </IconAlert>
          )}
        </>
      ) : (
        <TextLoading width={50} height={40} />
      )}
    </>
  );
};

export default NewPlannerForm;
